import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Search, MessageSquare, LogOut, Edit, Bell } from 'lucide-react';
import axios from 'axios';
import DeleteAccount from '../DeleteAccount/DeleteAccount';
import defaultUserImage from '../img/user.png';
import WebPushButton from '../WebPush/WebPushButton';
import { trackButtonClick } from "../../Components/Utils/analytics";
import fervoirLogo from '../img/Fervoir-Logo.png';
import { ayreneService } from '../../services/ayrene.service';
import { matchService } from '../../services/match.service';
import './Header.css';

interface HeaderProps {
  onSearchClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ onSearchClick }) => {
  const [profile, setProfile] = useState<any>(null);
  const [profilePictureUrl, setProfilePictureUrl] = useState<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const navigateTo = useNavigate();
  const location = useLocation();

  const toggleProfileDropdown = () => setDropdownOpen(!dropdownOpen);

  const handleLogout = async () => {
    console.log('🚪 Logout initiated from Header...');
    trackButtonClick('logout', 'header');

    try {
      console.log('🔄 Attempting Ayrene logout integration...');
      const ayreneResult = await ayreneService.logout();

      if (ayreneResult.success) {
        console.log('✅ Ayrene logout successful!');
      } else {
        console.warn('⚠️ Ayrene logout failed but continuing with Fervoir logout');
        console.warn('📝 Ayrene error message:', ayreneResult.message);
      }
    } catch (error) {
      console.error('❌ Ayrene logout integration error (non-blocking):', error);
    }

    console.log('🧹 Clearing localStorage and redirecting to login...');
    matchService.clearMatchCache();
    localStorage.removeItem("authToken");
    localStorage.removeItem("userId");
    localStorage.removeItem("ayreneAuthToken");
    localStorage.removeItem("ayreneRefreshToken");
    localStorage.removeItem("ayreneTokenExpiration");
    localStorage.removeItem("ayreneLoginStatus");
    navigateTo("/login");
  };

  const fetchProfilePicture = async () => {
    try {
      const response = await axios.get('https://nrimarriage.in/api/v1/users/Profile', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
      });

      if (response.status === 200) {
        setProfile(response.data);
        setProfilePictureUrl(response.data.profilePictureUrl || defaultUserImage);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setProfilePictureUrl(defaultUserImage);
    }
  };

  useEffect(() => {
    fetchProfilePicture();

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleEditProfile = (data: any) => {
    navigateTo('/editprofile', { state: { data } });
  };

  return (
    <header className="header">
      <div className="header-container">
        {/* Logo + Brand */}
        <div className="logo-container">
          <Link to="/home" className="logo99">
            <img
              src={fervoirLogo}
              alt="Fervoir logo"
              className="header-fervoir-logo"
            />
            <span className="logo-text">Fervoir</span>
          </Link>
        </div>

        {/* Right Section: Search, Notification, Chat, Profile */}
        <div className="header-actions">
          {/* Search */}
          <button
            className="action-button search-button"
            onClick={() => {
              trackButtonClick('search', 'header');
              onSearchClick();
            }}
            aria-label="Search"
          >
            <Search size={20} />
          </button>

          {/* Notification */}
          <button
            className="action-button notification-button"
            onClick={() => {
              trackButtonClick('notifications', 'header');
              console.log('Notifications clicked');
            }}
            aria-label="Notifications"
            title="Notifications"
          >
            <Bell size={20} />
          </button>

          {/* Chat / Messages */}
          <button
            className="action-button chat-button"
            onClick={() => {
              trackButtonClick('chat', 'header');
              matchService.clearMatchCache();
              navigateTo('/chat');
            }}
            aria-label="Chat"
            title="Chat"
          >
            <MessageSquare size={20} />
          </button>

          {/* Profile */}
          <div className="profile-container99" ref={dropdownRef}>
            <button
              className="profile-button"
              onClick={toggleProfileDropdown}
              aria-expanded={dropdownOpen}
            >
              <img
                src={profilePictureUrl || defaultUserImage}
                alt="Profile"
                className="profile-picture99"
              />
            </button>

            {dropdownOpen && (
              <div className="profile-dropdown">
                <div className="dropdown-item">
                  <button
                    className="profile-edit"
                    onClick={() => handleEditProfile(profile)}
                  >
                    <Edit size={16} />
                    <span>Edit Profile</span>
                  </button>
                </div>

                <div className="dropdown-item delete-account">
                  <DeleteAccount />
                </div>

                <div className="dropdown-item">
                  <WebPushButton
                    showText={true}
                    onSubscriptionChange={(isSubscribed) => {
                      console.log('Push notification subscription changed:', isSubscribed);
                    }}
                  />
                </div>

                <div className="dropdown-item">
                  <button
                    className="logout"
                    onClick={handleLogout}
                  >
                    <LogOut size={16} />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
